import apiClient from './api';
import type { MemberDeviceFields } from './device-display';

export type GroupType = 'static';

// Device display fields (machine_id, serial_number, number, …) are embedded by
// the group API so member rows render without a separate device fetch. They
// are optional until the backend ships them; see resolveMemberDevice.
export interface GroupMember extends MemberDeviceFields {
  device_id: string;
  added_at: string;
  is_internal: boolean;
}

export interface Group {
  id: string;
  name: string;
  type: GroupType;
  description: string;
  members: GroupMember[];
  created_at: string;
  updated_at: string;
}

export interface DeviceAlreadyInStaticGroupError {
  message: string;
  group: {
    id: string;
    name: string;
  };
}

export interface CreateGroupRequest {
  name: string;
  type: GroupType;
  description: string;
}

export interface UpdateGroupRequest {
  name: string;
  type: GroupType;
  description: string;
}

const base = '/admin/software-update/groups';

export const softwareUpdateGroupService = {
  list: async (): Promise<Group[]> => {
    return apiClient.get<Group[]>(base);
  },
  get: async (id: string): Promise<Group> => {
    return apiClient.get<Group>(`${base}/${id}`);
  },
  create: async (data: CreateGroupRequest): Promise<Group> => {
    return apiClient.post<Group>(base, data);
  },
  update: async (id: string, data: UpdateGroupRequest): Promise<Group> => {
    return apiClient.put<Group>(`${base}/${id}`, data);
  },
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`${base}/${id}`);
  },
  addDevice: async (id: string, deviceId: string): Promise<Group> => {
    return apiClient.post<Group>(`${base}/${id}/devices`, { device_id: deviceId });
  },
  removeDevice: async (id: string, deviceId: string): Promise<Group> => {
    return apiClient.delete<Group>(`${base}/${id}/devices/${deviceId}`);
  },
};

export default softwareUpdateGroupService;
